/**
 * NavigatorNodeComponent — recursive node dispatcher
 *
 * Renders any node from the navigator tree by switching on node.type.
 * To add a new component type:
 *   1. Add the component to registry.ts
 *   2. Add it to the `imports` array below
 *   3. Add a `*ngSwitchCase` line in the template
 */
import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

import { NavCrewRollCallComponent } from './nav/nav-crew-roll-call.component';
import {
    NavEventNavigatorComponent,
    NavStepComponent,
    NavCustomerInfoComponent,
    NavLocationInfoComponent,
    NavCheckboxComponent,
    NavInventoryEditorComponent,
    NavInventorySummaryComponent,
    NavCustomerSignatureComponent,
    NavJobNotesComponent,
    NavHomeProtectionComponent,
    NavStarRatingComponent,
    NavMovePerformanceSummaryComponent,
    NavPhotoCaptureComponent,
    NavPandaDocSignerComponent,
    NavLocationPermissionComponent,
    NavLoadProgressComponent,
    NavCollectPaymentComponent,
} from './registry';

export interface NavNode {
    type: string;
    props: Record<string, any>;
    children: NavNode[];
}

@Component({
    selector: 'app-navigator-node',
    standalone: true,
    imports: [
        CommonModule,
        NavEventNavigatorComponent,
        NavStepComponent,
        NavCustomerInfoComponent,
        NavLocationInfoComponent,
        NavCheckboxComponent,
        NavInventoryEditorComponent,
        NavInventorySummaryComponent,
        NavCustomerSignatureComponent,
        NavJobNotesComponent,
            NavHomeProtectionComponent,
        NavStarRatingComponent,
        NavMovePerformanceSummaryComponent,
        NavCrewRollCallComponent,
        NavPhotoCaptureComponent,
        NavPandaDocSignerComponent,
        NavLocationPermissionComponent,
        NavLoadProgressComponent,
        NavCollectPaymentComponent,
    ],
    styles: [`
        :host { display: contents; }
        app-nav-event-navigator { flex: 1 1 0; min-height: 0; }
    `],
    template: `
        <!-- Plain text / number node -->
        <ng-container *ngIf="isText(node)">{{ node }}</ng-container>

        <ng-container *ngIf="node && !isText(node)" [ngSwitch]="node.type">

            <!-- ── Structural ──────────────────────────────────────────────── -->
            <app-nav-event-navigator *ngSwitchCase="'EventNavigator'" [node]="node"></app-nav-event-navigator>
            <app-nav-step            *ngSwitchCase="'Step'"           [node]="node"></app-nav-step>

            <!-- ── Content blocks ─────────────────────────────────────────── -->
            <app-nav-customer-info          *ngSwitchCase="'CustomerInfo'"           [node]="node"></app-nav-customer-info>
            <app-nav-location-info          *ngSwitchCase="'LocationInfo'"           [node]="node"></app-nav-location-info>
            <app-nav-checkbox               *ngSwitchCase="'checkbox'"               [node]="node"></app-nav-checkbox>
            <app-nav-checkbox               *ngSwitchCase="'Checkbox'"               [node]="node"></app-nav-checkbox>
            <app-nav-inventory-editor       *ngSwitchCase="'inventory_editor'"       [node]="node"></app-nav-inventory-editor>
            <app-nav-inventory-editor       *ngSwitchCase="'InventoryEditor'"        [node]="node"></app-nav-inventory-editor>
            <app-nav-inventory-summary      *ngSwitchCase="'InventorySummary'"       [node]="node"></app-nav-inventory-summary>
            <app-nav-customer-signature     *ngSwitchCase="'customer_signature'"     [node]="node"></app-nav-customer-signature>
            <app-nav-customer-signature     *ngSwitchCase="'CustomerSignature'"      [node]="node"></app-nav-customer-signature>
            <app-nav-job-notes              *ngSwitchCase="'CrewNotes'"              [node]="node"></app-nav-job-notes>
            <app-nav-job-notes              *ngSwitchCase="'JobNotes'"               [node]="node"></app-nav-job-notes>
            <app-nav-home-protection        *ngSwitchCase="'HomeProtection'"         [node]="node"></app-nav-home-protection>
            <app-nav-star-rating            *ngSwitchCase="'StarRating'"             [node]="node"></app-nav-star-rating>
            <app-nav-move-performance-summary *ngSwitchCase="'MovePerformanceSummary'" [node]="node"></app-nav-move-performance-summary>
            <app-nav-crew-roll-call          *ngSwitchCase="'CrewRollCall'"           [node]="node"></app-nav-crew-roll-call>
            <app-nav-photo-capture           *ngSwitchCase="'PhotoCapture'"           [node]="node"></app-nav-photo-capture>
            <app-nav-pandadoc-signer         *ngSwitchCase="'PandaDocSigner'"         [node]="node"></app-nav-pandadoc-signer>
            <app-nav-location-permission     *ngSwitchCase="'LocationPermission'"     [node]="node"></app-nav-location-permission>
            <app-nav-load-progress          *ngSwitchCase="'LoadProgress'"           [node]="node"></app-nav-load-progress>
            <app-nav-collect-payment         *ngSwitchCase="'CollectPayment'"        [node]="node"></app-nav-collect-payment>

            <!-- ── Primitives ─────────────────────────────────────────────── -->
            <button *ngSwitchCase="'button'" style="display:flex;align-items:center;gap:0.5rem;width:100%;padding:1rem;
                           margin-bottom:0.75rem;background:white;border:1.5px solid #ced4da;
                           border-radius:8px;font-size:1rem;cursor:pointer;"
                    (click)="node.props?.click?.()">
                <i *ngIf="node.props?.icon" [class]="node.props.icon"></i>
                {{ node.props?.label }}
            </button>
            <button *ngSwitchCase="'Button'" style="display:flex;align-items:center;gap:0.5rem;width:100%;padding:1rem;
                           margin-bottom:0.75rem;background:white;border:1.5px solid #ced4da;
                           border-radius:8px;font-size:1rem;cursor:pointer;"
                    (click)="node.props?.click?.()">
                <i *ngIf="node.props?.icon" [class]="node.props.icon"></i>
                {{ node.props?.label }}
            </button>
            <p *ngSwitchCase="'p'" style="font-size:0.9rem;color:#666;white-space:pre-line;
                      margin:0.35rem 0 0.75rem;line-height:1.55;" [class]="node.props?.className">{{ node.props?.text }}</p>
            <p *ngSwitchCase="'P'" style="font-size:0.9rem;color:#666;white-space:pre-line;
                      margin:0.35rem 0 0.75rem;line-height:1.55;" [class]="node.props?.className">{{ node.props?.text }}</p>
            <div *ngSwitchCase="'div'" [class]="node.props?.className">
                <app-navigator-node *ngFor="let c of node.children" [node]="c"></app-navigator-node>
            </div>

            <!-- ── Unknown fallback ───────────────────────────────────────── -->
            <div *ngSwitchDefault style="padding:0.5rem;color:#c00;font-size:0.85rem;background:#fee;
                                border-radius:6px;margin-bottom:0.5rem;">
                Unknown: <code>{{ node.type }}</code>
            </div>

        </ng-container>
    `,
})
export class NavigatorNodeComponent {
    @Input() node: any;
    isText(n: any): boolean { return typeof n === 'string' || typeof n === 'number'; }
}
